import { useState, useEffect } from "react";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

const API_BASE = "https://sattva-backend.demetrix.in";

const LearningVideo = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [editorToken, setEditorToken] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("Editor_token");
    setEditorToken(token);

    const fetchVideos = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/videos`);
        const data = await res.json();

        if (data.status === "success" && Array.isArray(data.data)) {
          const active = data.data.filter((v) => !v.is_deleted);
          setVideos(active);
        } else {
          setVideos([]);
        }
      } catch (err) {
        console.error("Failed to fetch videos:", err);
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const isEditor = Boolean(editorToken);

  const getYoutubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const generateThumbnailUrl = (videoUrl) => {
    const videoId = getYoutubeId(videoUrl);
    if (!videoId) return "";
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  };

  const handleDelete = async (slug) => {
    if (!isEditor) {
      alert("You are not authorized to delete videos.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this video? This action cannot be undone.")) return;

    try {
      const res = await fetch(`${API_BASE}/videos/${slug}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${editorToken}`,
        },
      });

      const result = await res.json();

      if (result.status === "success") {
        setVideos((prev) => prev.filter((v) => v.slug !== slug));
      } else {
        alert(result.message || "Failed to delete video");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting video");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditor) {
      alert("You are not authorized to save videos.");
      return;
    }

    const videoUrl = editingVideo.videoUrl?.trim();
    if (!videoUrl) {
      alert("YouTube URL is required.");
      return;
    }

    const videoId = getYoutubeId(videoUrl);
    if (!videoId) {
      alert("Invalid YouTube URL. Please provide a valid link.");
      return;
    }

    const thumbnailUrl = generateThumbnailUrl(videoUrl);

    const payload = {
      title: editingVideo.title?.trim() || "",
      description: (editingVideo.description || "").trim(),
      video_url: videoUrl,
      thumbnail_url: thumbnailUrl,
      is_featured: editingVideo.is_featured ?? false,
    };

    const isEdit = !!editingVideo.slug && videos.some((v) => v.slug === editingVideo.slug);
    const url = isEdit
      ? `${API_BASE}/videos/${editingVideo.slug}`
      : `${API_BASE}/videos`;

    const method = isEdit ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${editorToken}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.status === "success") {
        const refreshRes = await fetch(`${API_BASE}/videos`);
        const refreshData = await refreshRes.json();
        if (refreshData.status === "success" && Array.isArray(refreshData.data)) {
          setVideos(refreshData.data.filter((v) => !v.is_deleted));
        }
        setModalOpen(false);
        setEditingVideo(null);
        alert(result.message);
      } else {
        alert(result.message || result.error?.[0]?.error || "Failed to save video");
      }
    } catch (err) {
      console.error(err);
      alert("Error saving video");
    }
  };

  if (loading) {
    return (
      <section className="py-12 px-5">
        <div className="max-w-7xl mx-auto text-center text-gray-500 dark:text-gray-400">
          Loading videos...
        </div>
      </section>
    );
  }

  return (
    <>
      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-wrapper {
          overflow: hidden;
          width: 100%;
          padding: 0 2rem;          
        }
        .marquee {
          display: flex;
          gap: 24px;
          width: max-content;
          animation: marquee 24s linear infinite;
        }
        .marquee:hover {
          animation-play-state: paused;
        }
        .video-card {
          width: 280px;
          border-radius: 16px;
          overflow: hidden;
          background: white;
          border: 1px solid #e5e7eb;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          position: relative;
          flex-shrink: 0;
          transition: all 0.2s;
        }
        .video-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          transform: translateY(-2px);
        }
        .video-title {
          padding: 12px 16px;
          font-weight: 600;
          font-size: 0.95rem;
          color: #111827;
        }
        @media (max-width: 640px) {
          .marquee-wrapper {
            padding: 0 1rem;        
          }
        }
      `}</style>

      <section className="py-12 px-4">
        <div className="container ">
          {/* Centered title + add button on right */}
          <div className="relative mb-10 ">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white">
              Videos
            </h2>

            {isEditor && (
              <div className="absolute right-0 top-1/2 -translate-y-1/2">
                <button
                  onClick={() => {
                    setEditingVideo({
                      title: "",
                      videoUrl: "",
                      description: "",
                      is_featured: false,
                    });
                    setModalOpen(true);
                  }}
                  className="p-2.5 rounded-full bg-green-100 text-green-700 hover:bg-green-200 transition"
                  title="Add Video"
                >
                  <PlusIcon className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {videos.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              No videos available at the moment.
            </div>
          ) : (
            <div className="marquee-wrapper">
              <div className="marquee">
                {Array(4).fill(videos).flat().map((video, index) => {   // ← increased to 4 copies
                  const videoId = getYoutubeId(video.video_url);
                  if (!videoId) return null;

                  const thumbnail = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

                  return (
                    <div key={`${video.slug || 'video'}-${index}`} className="video-card group">
                      <div className="relative">
                        <img
                          src={thumbnail}
                          alt={video.title}
                          className="w-full h-44 object-cover cursor-pointer"
                          onClick={() => {
                            setSelectedVideo(video);
                            setIsVideoOpen(true);
                          }}
                        />

                        {isEditor && (
                          <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingVideo({
                                  ...video,
                                  videoUrl: video.video_url || "",
                                  description: video.description || "",
                                  is_featured: video.is_featured ?? false,
                                });
                                setModalOpen(true);
                              }}
                              className="p-1.5 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-700"
                              title="Edit"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(video.slug);
                              }}
                              className="p-1.5 rounded-full bg-red-100 hover:bg-red-200 text-red-700"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="video-title">{video.title}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>


      {isVideoOpen && selectedVideo && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
            <button
              onClick={() => setIsVideoOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white text-2xl z-10"
            >
              ✕
            </button>

            <div className="p-6 pb-4">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {selectedVideo.title}
              </h3>
            </div>

            <iframe
              src={`https://www.youtube.com/embed/${getYoutubeId(selectedVideo.video_url)}?autoplay=1&rel=0`}
              allow="autoplay; encrypted-media"
              allowFullScreen
              className="w-full aspect-video"
              title={selectedVideo.title}
            />
          </div>
        </div>
      )}


      {modalOpen && editingVideo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg relative max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-5 text-gray-900 dark:text-white">
              {editingVideo.slug ? "Edit Video" : "Add Video"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4 text-slate-600">
              <input
                type="text"
                placeholder="Title *"
                value={editingVideo.title || ""}
                onChange={(e) =>
                  setEditingVideo({ ...editingVideo, title: e.target.value })
                }
                className="w-full border border-gray-300 dark:border-gray-700 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-900 dark:text-white"
                required
              />

              <input
                type="text"
                placeholder="YouTube URL (full link) *"
                value={editingVideo.videoUrl || ""}
                onChange={(e) =>
                  setEditingVideo({ ...editingVideo, videoUrl: e.target.value })
                }
                className="w-full border border-gray-300 dark:border-gray-700 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-900 dark:text-white"
                required
              />

              <textarea
                placeholder="Description (optional)"
                value={editingVideo.description || ""}
                onChange={(e) =>
                  setEditingVideo({ ...editingVideo, description: e.target.value })
                }
                rows={4}
                className="w-full border border-gray-300 dark:border-gray-700 rounded-lg p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-900 dark:text-white"
              />

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setModalOpen(false);
                    setEditingVideo(null);
                  }}
                  className="px-5 py-2.5 rounded-lg text-sm font-medium bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default LearningVideo;